import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Checkbox,
  Chip,
  Pagination,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { Box } from '@mui/system';
import {
  DEFAULT_PAGINATION_QUERY,
} from 'api/common.const';
import { ListDropdown } from 'api/common.types';
import FiltersDialog from 'components/permission/filters_dialog';
import PermissionCard from 'components/permission/permission_card';
import { OrderDialog } from 'components/shared';
import ContainerPainel from 'components/container_painel';
import { changePermissionStatus, deletePermission, getModulesList, getPermissionsList } from 'api/permission/manage-pemission.api';
import { getGroupsDropdown } from 'api/group/group.api';
import { ROUTES } from 'routes/constants';
import { useNavigate } from 'react-router-dom';
import DeleteRedIcon from '../../assets/icons/delete-red-icon.svg';
import { Module } from 'types/permission';
import { useAuth } from 'contexts/auth/auth_context';

const administratorsList: string[] = ['ADMINISTRADOR'];
const PermissionsList: React.FC = () => {
  const loadFiltersFromLocalStorage = () => {
    const storagedFilters = localStorage.getItem("permissionFilters");
    return storagedFilters
      ? JSON.parse(storagedFilters)
      : DEFAULT_PAGINATION_QUERY;
  };

  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdministrator = administratorsList.includes(user!.permission.name);

  useEffect(() => {
    if (!isAdministrator) {
      navigate(ROUTES.HOME);
    }
  }, [isAdministrator, navigate]);

  const [filters, setFilters] = useState<Partial<any>>(
    () => {
      const saved = loadFiltersFromLocalStorage();
      return {
        ...saved,
        sortBy: saved.sortBy || 'name',
      };
    },
  );
  const [orderBy, setOrderBy] = useState<string>('ASC');
  const [toggleOrderDialog, setToggleOrderDialog] = useState(false);
  const [toggleFiltersDialog, setToggleFiltersDialog] = useState(false);
  const [groupsDropdown, setGroupsDropdown] = useState<ListDropdown[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<string | null>(null);
  const [modules, setModules] = useState<Module[]>([]);

  useEffect(() => {
    const fetchModules = async () => {
      const result = await getModulesList({});
      setModules((result.data.filter((module: Module) => module.name !== 'OTHERS')));
    };

    fetchModules();
  }, []);


  const fetchPermissionsList = async () => {
    try {
      const result = await getPermissionsList(filters);
      const totalPages = Math.ceil(
        result.info.count / parseInt(filters.take || '10', 10),
      );
      setTotalPages(totalPages);
      return result;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return null;
    }
  };

  const { data: response, refetch } = useQuery(
    ['permissionsList', filters],
    fetchPermissionsList,
    {
      enabled: !!filters,
      keepPreviousData: true,
      retry: 1,
    },
  );

  const applyFilters = async (newFilters: Partial<any>) => {
    try {
      const result = await getPermissionsList(newFilters);

      if (result && result.info.count > 0) {
        localStorage.setItem(
          "permissionFilters",
          JSON.stringify(newFilters),
        );
        setFilters(newFilters);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error applying filters:', error);
      return false;
    }
  };

  const hasAppliedFilters = () => {
    const { name, group, description } = filters;
    return !!(name || group || description);
  };

  const handleClearFilters = () => {
    localStorage.setItem(
      "permissionFilters",
      JSON.stringify(DEFAULT_PAGINATION_QUERY),
    );
    setFilters(DEFAULT_PAGINATION_QUERY);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const newFilters: Partial<any> = {
      ...filters,
      orderBy: value,
    };
    localStorage.setItem(
      "permissionFilters",
      JSON.stringify(newFilters),
    );
    setOrderBy(value);
  };

  const applySort = () => {
    const updatedFilters = {
      ...filters,
      sortBy: 'name',
      orderBy,
    };
    localStorage.setItem(
      "permissionFilters",
      JSON.stringify(updatedFilters),
    );
    setFilters(updatedFilters);
    setToggleOrderDialog(false);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && response?.data) {
      const permissionIds = response?.data?.map((permission: any) => permission.uuid);
      setSelectedPermissions(permissionIds);
    } else {
      setSelectedPermissions([]);
    }
  };

  const handleSelectPermission = (permissionId: string, checked: boolean) => {
    setSelectedPermissions((prevSelected) =>
      checked
        ? [...prevSelected, permissionId]
        : prevSelected.filter((id) => id !== permissionId),
    );
  };

  const handleToggleOrderDialog = () => {
    setToggleOrderDialog(!toggleOrderDialog);
  };

  const handleToggleFiltersDialog = () => {
    setToggleFiltersDialog(!toggleFiltersDialog);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    setFilters((prevFilters: any) => ({
      ...prevFilters,
      page: String(page),
    }));
  };

  const handleEditPermission = (permissionId: string) => {
    navigate(ROUTES.PERMISSIONS_EDIT.replace(':uuid', permissionId))
  };

  const handleViewPermission = (permissionId: string) => {
    navigate(ROUTES.PERMISSIONS_VIEW.replace(':uuid', permissionId))
  };

  const handleDeleteClick = (permissionId: string) => {
    setPermissionToDelete(permissionId);
    setDeleteDialogOpen(true);
  };

  const handleChangeStatus = async (permissionId: string, isActive: boolean) => {
    try {
      await changePermissionStatus(permissionId, !isActive)
      await refetch();
    } catch (error) {
      alert(`Não foi possível concluir a alteração do status do perfil`);
      console.error('Error toggling permission status:', error);
    }
  }

  const handleConfirmDelete = async () => {
    if (permissionToDelete) {
      try {
        await deletePermission(permissionToDelete);
        await refetch();
        setDeleteDialogOpen(false);
        setPermissionToDelete(null);
      } catch (error) {
        alert(`Este perfil não pode ser excluído, pois está associado a um ou mais usuários do sistema. Para prosseguir com a exclusão, é necessário desvincular todos os usuários relacionados a este perfil.`);
        console.error('Error deleting permission:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPermissionToDelete(null);
  };

  useEffect(() => {
    const savedFilters = loadFiltersFromLocalStorage();
    if (savedFilters) {
      setFilters(savedFilters);
    }

    const fetchDropdowns = async () => {
      const result = await getGroupsDropdown();
      setGroupsDropdown(result.data);
    };

    fetchDropdowns();
  }, []);

  return (
    <ContainerPainel pagina='Gerenciar Perfis' showBackButton={true}>
      <Box
        sx={{
          backgroundColor: '#0048B6',
          height: '190px',
          display: 'flex',
          justifyContent: 'center',
          p: '0.5rem',
        }}
      >
        <Box sx={{ width: '1552px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label='Ordernar'
                onClick={handleToggleOrderDialog}
                onDelete={handleToggleOrderDialog}
                deleteIcon={<ArrowDropDownIcon style={{ color: '#FFFFFF' }} />}
                variant='outlined'
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  marginRight: '10px',
                }}
              />
              <Chip
                label='Filtrar'
                onClick={handleToggleFiltersDialog}
                onDelete={handleToggleFiltersDialog}
                deleteIcon={
                  <ArrowDropDownIcon
                    style={{
                      color: hasAppliedFilters() ? '#0048B6' : '#FFFFFF',
                    }}
                  />
                }
                variant='outlined'
                sx={{
                  color: hasAppliedFilters() ? '#0048B6' : '#FFFFFF',
                  backgroundColor: hasAppliedFilters() ? '#FFFFFF' : '#0048B6',
                  border: '1px solid #FFFFFF',
                }}
              />
              {hasAppliedFilters() && (
                <Button
                  variant='text'
                  startIcon={<CloseIcon />}
                  sx={{ color: '#FFFFFF', marginLeft: '10px' }}
                  onClick={handleClearFilters}
                >
                  Limpar filtros
                </Button>
              )}
            </Box>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              sx={{ color: '#FFFFFF', border: '1px solid #FFFFFF' }}
              onClick={() => navigate(ROUTES.PERMISSIONS_CREATE)}
            >
              CADASTRAR PERFIL
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingBottom: '10px',
              mt: 2
            }}
          >
            <Checkbox
              sx={{ color: '#FFFFFF' }}
              checked={
                selectedPermissions.length === (response?.data?.length || 0) &&
                response?.data?.length > 0
              }
              onChange={handleSelectAll}
            />

            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '18px',
                lineHeight: '27px',
              }}
            >
              Selecionar todos | {selectedPermissions.length} de{' '}
              {response?.data?.length || 0} selecionado(s)
            </Typography>
          </Box>

          {response?.data?.map((permission: any) => {
            return (
              <PermissionCard
                permission={permission}
                key={permission.uuid}
                isChecked={selectedPermissions.includes(permission.uuid)}
                onCheckboxChange={(event) => handleSelectPermission(permission.uuid, event.target.checked)}
                onEdit={() => handleEditPermission(permission.uuid)}
                onView={() => handleViewPermission(permission.uuid)}
                onDelete={() => handleDeleteClick(permission.uuid)}
                onToggleStatus={() => handleChangeStatus(permission.uuid, permission.is_active)}
              />
            );
          })}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '20px',
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              color='primary'
              onChange={handlePageChange}
            />
            <Box display="flex" alignItems="center" gap={1} ml={2}>
              <Typography variant="body2" >Itens por página:</Typography>
              <Select
                value={filters.take || '10'}
                onChange={(e) => setFilters({ ...filters, take: String(e.target.value) })}
                size="small"
                sx={{
                  backgroundColor: '#FFFFFF',
                  '& .MuiSelect-icon': {
                    color: '#0048B6'
                  }
                }}
              >
                {[5, 10, 25, 50].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dialogs */}
      <OrderDialog
        open={toggleOrderDialog}
        selectedSort={orderBy}
        onClose={() => setToggleOrderDialog(false)}
        handleSortChange={handleSortChange}
        applySort={applySort}
      />

      <FiltersDialog
        open={toggleFiltersDialog}
        onClose={() => setToggleFiltersDialog(false)}
        initialFilter={{
          initialName: filters?.initialName,
          initialGroup: filters?.initialGroup,
          initialModule: filters?.initialModule,
          initialStatus: filters?.initialStatus
        }}
        applyFilters={applyFilters}
        typesDropdown={groupsDropdown}
        modules={modules}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <img src={DeleteRedIcon} alt='Delete Icon' />
          <Typography sx={{ fontSize: '24px', marginTop: '20px' }}>
            Confirmar Exclusão
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            Tem certeza que deseja excluir esta permissão? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}
          >
            CONFIRMAR EXCLUSÃO
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancelDelete}
            sx={{ color: '#0048B6', borderColor: '#0048B6' }}
          >
            CANCELAR
          </Button>
        </DialogActions>
      </Dialog>
    </ContainerPainel>
  );
};

export default PermissionsList;